import { formatDate } from "@angular/common";
import { ChangeDetectorRef, Component, HostListener, Inject, OnInit } from "@angular/core";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { hasPermissionByKey } from "@app/_helpers/permission";
import { createRxValue, fromRxValue } from "@app/_helpers/utils";
import { ComeAndGoAbsenceDialogComponent } from "@app/components/come-and-go-absence-dialog/come-and-go-absence-dialog.component";
import { ComegoService, ComegoTime, UserSettingsQuery } from "@timeghost/web-api";
import { isSameDay, isValid, parse } from "date-fns/esm";
import { map } from "rxjs";
import {
	ComeAndGoMultiUpdateDialogComponent,
	WorkingHoursUpdateData,
} from "../come-and-go-multi-update-dialog/come-and-go-update-dialog.component";
import { AbsenceConflictInterval, AbsenceConflictRange, computeAbsenceConflicts } from "./absence-conflict.utils";

export type AbsenceConflictCloseResult = "overwrite" | "skip" | "cancel";

export interface AbsenceConflictDialogData {
	conflicts: Map<string, Set<string>>;
	absenceIntervalsByUser: Map<string, AbsenceConflictInterval[]>;
	range: AbsenceConflictRange;
	hideSkip?: boolean;
	parent: ComeAndGoAbsenceDialogComponent;
}

export type AbsenceConflictDateGroup = {
	date: Date;
	entries: ComegoTime[];
};

export type AbsenceConflictRow = {
	userId: string;
	name: string;
	dateGroups: AbsenceConflictDateGroup[];
};

type AbsenceSubcategory = {
	id: string;
	enabled: boolean;
	title: string;
	visibleUsers: string[];
	visibleForGroups: string[];
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
};
const COMEGO_ICON_MAP = {
	work: "work_history",
	pause: "pause",
	absence: "beach_access",
	not_worked: "home_filled",
};

@Component({
	selector: "tg-absence-conflict-dialog",
	templateUrl: "./absence-conflict-dialog.component.html",
	styleUrls: ["./absence-conflict-dialog.component.scss"],
})
export class AbsenceConflictDialogComponent implements OnInit {
	conflictRows: AbsenceConflictRow[] = [];
	readonly canDeleteTimes = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)))
	);
	readonly hideSkip = createRxValue(this.data.hideSkip);
	readonly iconMap = COMEGO_ICON_MAP;
	absenceSubcategories: AbsenceSubcategory[] = [];
	readonly canUpdateTimes$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)));
	constructor(
		@Inject(MAT_DIALOG_DATA) private readonly data: AbsenceConflictDialogData,
		private readonly ref: MatDialogRef<AbsenceConflictDialogComponent, AbsenceConflictCloseResult>,
		private userSettingsQuery: UserSettingsQuery,
		private dialog: MatDialog,
		private comegoService: ComegoService,
		private changeDetectorRef: ChangeDetectorRef
	) {
		this.refreshConflictRows();
	}

	@HostListener("document:keydown.escape")
	onEscape() {
		this.close("cancel");
	}

	close(result: AbsenceConflictCloseResult) {
		this.ref.close(result);
	}
	ngOnInit() {
		const user = this.userSettingsQuery.getValue();
		this.absenceSubcategories =
			(user?.workspace as { workinghours?: { absenceSubcategories: typeof this.absenceSubcategories } })
				?.workinghours?.absenceSubcategories || [];
	}

	private dayKey(d: Date): string {
		return formatDate(d, "yyyy-MM-dd", "en-US");
	}

	private refreshConflictRows(): void {
		this.conflictRows = this.buildConflictRows();
		this.changeDetectorRef.markForCheck();
		if (this.data.conflicts.size === 0) {
			this.close("cancel");
		}
	}

	private async revalidateConflicts(): Promise<void> {
		const { range, absenceIntervalsByUser } = this.data;
		if (!range?.from || !range?.to || absenceIntervalsByUser.size === 0) {
			this.data.conflicts = new Map();
			this.data.parent.allWorkinghours$.update([]);
			this.refreshConflictRows();
			return;
		}

		const userIds = [...absenceIntervalsByUser.keys()];
		try {
			const workingTimes = await this.comegoService.getBetween(range.from, range.to);
			const filtered = workingTimes.filter((wh) => userIds.includes(wh.user.id));
			const { conflicts, relevantWorkingHours } = computeAbsenceConflicts(filtered, absenceIntervalsByUser);
			this.data.conflicts = conflicts;
			this.data.parent.allWorkinghours$.update(relevantWorkingHours);
			this.refreshConflictRows();
		} catch (error) {
			pushError(error);
		}
	}

	private buildConflictRows(): AbsenceConflictRow[] {
		const rows: AbsenceConflictRow[] = [];
		for (const [userId, days] of this.data.conflicts.entries()) {
			const sortedDays = [...days].sort();
			const dateGroups: AbsenceConflictDateGroup[] = sortedDays.map((dayKey) => {
				const date = parse(dayKey, "yyyy-MM-dd", new Date());
				const entries = this.data.parent.allWorkinghours$.value
					.filter((wh) => wh.user.id === userId && isSameDay(new Date(wh.start), date))
					.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
				return { date, entries };
			});
			const user = this.userSettingsQuery.getValue().workspace.users.find((u) => u.id === userId);
			rows.push({
				userId,
				name: user?.name ?? "",
				dateGroups,
			});
		}
		return rows.sort((a, b) => a.name.localeCompare(b.name));
	}
	resolveSubcategoryTitle(typeId: string): string {
		return this.absenceSubcategories.find((s) => s.id === typeId)?.title ?? "";
	}

	calculateExistingEntryDuration(entry: ComegoTime): number {
		if (!entry.start || !entry.end) {
			return 0;
		}
		const start = typeof entry.start === "string" ? new Date(entry.start) : entry.start;
		const end = typeof entry.end === "string" ? new Date(entry.end) : entry.end;
		return (end.getTime() - start.getTime()) / 1000;
	}

	openUpdateMultiDialog(date: Date, userId: string) {
		if (!date || !isValid(date)) return;
		const selectedUser = this.userSettingsQuery.getValue().workspace.users.find((u) => u.id === userId);
		if (!selectedUser) return;

		const entries = this.data.parent.allWorkinghours$.value.filter(
			(wh) => wh.user.id === userId && isSameDay(new Date(wh.start), date)
		);

		this.dialog
			.open(ComeAndGoMultiUpdateDialogComponent, {
				data: <WorkingHoursUpdateData>{
					time: {
						value: entries.length ? entries : ([{ user: selectedUser }] as unknown as ComegoTime[]),
						date,
						refetchFromDate: true,
					},
					forceRecreateDay: true,
				},
			})
			.afterClosed()
			.subscribe(() => {
				void this.revalidateConflicts();
			});
	}

	async deleteExistingEntries(date: Date, userId: string) {
		if (!date || !isValid(date)) return;
		const entries = this.data.parent.allWorkinghours$.value.filter(
			(wh) => wh.user.id === userId && isSameDay(new Date(wh.start), date)
		);
		if (!entries.length) return;

		try {
			await this.comegoService.delete(...entries.map((e) => ({ id: e.id })));
			await this.revalidateConflicts();
		} catch (error) {
			pushError(error);
		}
	}
}
